import React from 'react';
import { useNavigate } from 'react-router-dom';

const Welcome: React.FC = () => {
  const navigate = useNavigate();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="welcome-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          <div className="navbar-actions">
            <button 
              onClick={() => scrollToSection('features')}
              className="btn btn-ghost"
            >
              Features
            </button>
            <button 
              onClick={() => navigate('about')}
              className="btn btn-ghost"
            >
              About
            </button>
            <button 
              onClick={() => navigate('/signin')}
              className="btn btn-ghost"
            >
              Sign in
            </button>
            <button 
              onClick={() => navigate('/signup')}
              className="btn btn-primary"
            >
              Sign up
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <h1 className="hero-title">
            Streamline Your Academic Projects
          </h1>
          <p className="hero-description">
            Connect students with professional freelancers for SIWES reports, 
            project documentation, and academic presentations. Quality work, 
            seamless collaboration, and timely delivery.
          </p>
          <div className="hero-actions">
            <button 
              onClick={() => navigate('/signup')}
              className="btn btn-primary btn-lg"
            >
              Start Your Journey
            </button>
            <button 
              onClick={() => navigate('/signin')}
              className="btn btn-secondary btn-lg"
            >
              I Have an Account
            </button>
          </div>
          
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">500+</div>
              <div className="stat-label">Projects Completed</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">200+</div>
              <div className="stat-label">Active Freelancers</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">95%</div>
              <div className="stat-label">Success Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="features-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose NexSync?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to manage your academic projects efficiently
            </p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3 className="feature-title">Quick Setup</h3>
              <p className="feature-description">
                Get started in minutes with our intuitive platform designed for students and freelancers.
                No complex setup required.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3 className="feature-title">Secure Collaboration</h3>
              <p className="feature-description">
                Your projects and data are protected with enterprise-grade security measures.
                End-to-end encryption for all file transfers.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">💼</div>
              <h3 className="feature-title">Professional Results</h3>
              <p className="feature-description">
                Connect with verified freelancers specializing in academic projects and reports.
                Quality guaranteed with our review system.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">⏱️</div>
              <h3 className="feature-title">Fast Delivery</h3>
              <p className="feature-description">
                Get your projects completed on time with our efficient workflow management.
                Never miss another deadline.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3 className="feature-title">Transparent Pricing</h3>
              <p className="feature-description">
                No hidden fees. Clear pricing for all services with multiple payment options.
                Get the best value for your budget.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📞</div>
              <h3 className="feature-title">24/7 Support</h3>
              <p className="feature-description">
                Our support team is always available to help you with any issues or questions.
                We're here when you need us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="features-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Simple steps to get your academic projects completed
            </p>
          </div>

          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3 className="step-title">Sign Up & Create Profile</h3>
                <p className="step-description">
                  Register as a student or freelancer. Complete your profile with 
                  necessary details and verification.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3 className="step-title">Post Your Project</h3>
                <p className="step-description">
                  Describe your project requirements, upload relevant files, 
                  and set your budget and deadline.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3 className="step-title">Get Matched</h3>
                <p className="step-description">
                  Our system matches you with qualified freelancers based on 
                  your project requirements and their expertise.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3 className="step-title">Collaborate & Review</h3>
                <p className="step-description">
                  Work directly with your assigned freelancer, track progress, 
                  and provide feedback throughout the process.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3 className="step-title">Receive & Approve</h3>
                <p className="step-description">
                  Get your completed project, review the quality, and approve 
                  the work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="features-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Our Users Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join thousands of satisfied students and freelancers
            </p>
          </div>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                "NexSync helped me complete my SIWES report in record time. 
                The freelancer was professional and delivered quality work."
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">A</div>
                <div className="author-info">
                  <div className="author-name">Aisha Mohammed</div>
                  <div className="author-role">Computer Science Student</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                "As a freelancer, NexSync has provided me with consistent projects 
                and reliable payments. The platform is easy to use."
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">J</div>
                <div className="author-info">
                  <div className="author-name">John Adeyemi</div>
                  <div className="author-role">Academic Writer</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                "The project tracking feature is amazing! I could see the progress 
                of my presentation slides in real-time."
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">C</div>
                <div className="author-info">
                  <div className="author-name">Chiamaka Okoro</div>
                  <div className="author-role">Engineering Student</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <div className="cta-content">
            <h2 className="cta-title">
              Ready to Transform Your Academic Projects?
            </h2>
            <p className="cta-description">
              Join hundreds of students and freelancers already using NexSync
            </p>
            <div className="cta-actions">
              <button 
                onClick={() => navigate('/signup')}
                className="btn btn-primary btn-lg"
              >
                Start Your Journey
              </button>
              <button 
                onClick={() => navigate('/signin')}
                className="btn btn-secondary btn-lg"
              >
                I Have an Account
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="navbar-brand">
                <div className="brand-logo">NS</div>
                <span className="brand-text">NexSync</span>
              </div>
              <p className="footer-description">
                Empowering students and freelancers through seamless collaboration.
              </p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Platform</h4>
                <button onClick={() => scrollToSection('features')} className="footer-link">Features</button>
                <button onClick={() => navigate('/about')} className="footer-link">About</button>
                <button onClick={() => navigate('/signup')} className="footer-link">Pricing</button>
              </div>
              
              <div className="footer-column">
                <h4>Support</h4>
                <button onClick={() => navigate('/helpcenter')} className="footer-link">Help Center</button>
                <button onClick={() => navigate('/contactus')} className="footer-link">Contact Us</button>
                <button onClick={() => navigate('/documentation')} className="footer-link">Documentation</button>
              </div>
              
              <div className="footer-column">
                <h4>Legal</h4>
                <button onClick={() => navigate('/privacypolicy')} className="footer-link">Privacy Policy</button>
                <button onClick={() => navigate('/termsofservice')} className="footer-link">Terms of Service</button>
                <button onClick={() => navigate('/cookiepolicy')} className="footer-link">Cookie Policy</button>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2025 NexSync. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Welcome;